export type SubscriptionGateReason =
  | "active"
  | "trialing"
  | "trial_expired"
  | "cancelled"
  | "payment_failed"
  | "suspended"
  | "inactive"
  | "unknown";

export interface SubscriptionAccessInput {
  subscriptionStatus: string | null;
  trialExpiresAt: Date | null;
  firmStatus: string | null;
  firmSuspended: boolean;
}

export interface SubscriptionAccessState {
  allowed: boolean;
  reason: SubscriptionGateReason;
  statusLabel: string;
  recoveryParam: string | null;
}

export type SubscriptionManagementAction = "checkout" | "portal" | "reconcile" | "cancel";

// Canonical subscriptionStatus values that grant workspace access. `demo` is the
// derived status for `demo_active` tenants (Blueprint §3.1 deriveFirmFlags).
const ACTIVE_STATUSES = new Set(["active", "trialing", "demo"]);
const CANCELLED_STATUSES = new Set(["cancelled", "canceled"]);
const PAYMENT_FAILED_STATUSES = new Set(["past_due", "unpaid", "payment_failed", "incomplete", "incomplete_expired"]);

export function resolveSubscriptionAccess(input: SubscriptionAccessInput): SubscriptionAccessState {
  const firmStatus = (input.firmStatus ?? "").toLowerCase();
  const subscriptionStatus = (input.subscriptionStatus ?? "").toLowerCase();
  const now = Date.now();
  const trialExpired = input.trialExpiresAt ? input.trialExpiresAt.getTime() < now : false;
  const trialActive = input.trialExpiresAt ? input.trialExpiresAt.getTime() >= now : false;

  if (input.firmSuspended || firmStatus === "suspended") {
    return { allowed: false, reason: "suspended", statusLabel: "Firm suspended", recoveryParam: "suspended=1" };
  }

  if (firmStatus === "inactive" && !ACTIVE_STATUSES.has(subscriptionStatus)) {
    return { allowed: false, reason: "inactive", statusLabel: "Firm inactive", recoveryParam: "subscription_cancelled=1" };
  }

  if (ACTIVE_STATUSES.has(subscriptionStatus)) {
    return { allowed: true, reason: subscriptionStatus === "trialing" ? "trialing" : "active", statusLabel: subscriptionStatus === "trialing" ? "Trial active" : "Active", recoveryParam: null };
  }

  if (PAYMENT_FAILED_STATUSES.has(subscriptionStatus)) {
    return { allowed: false, reason: "payment_failed", statusLabel: "Payment failed", recoveryParam: "payment_failed=1" };
  }

  if (CANCELLED_STATUSES.has(subscriptionStatus)) {
    return { allowed: false, reason: "cancelled", statusLabel: "Subscription cancelled", recoveryParam: "subscription_cancelled=1" };
  }

  if (trialExpired) {
    return { allowed: false, reason: "trial_expired", statusLabel: "Trial expired", recoveryParam: "trial_expired=1" };
  }

  if (trialActive) {
    return { allowed: true, reason: "trialing", statusLabel: "Trial active", recoveryParam: null };
  }

  if (!subscriptionStatus) {
    return { allowed: true, reason: "unknown", statusLabel: "Subscription status pending", recoveryParam: null };
  }

  return { allowed: false, reason: "inactive", statusLabel: "Subscription inactive", recoveryParam: "subscription_cancelled=1" };
}

export function isBillingRecoveryPath(pathname: string): boolean {
  return pathname === "/settings" || pathname === "/billing";
}

export function billingRecoverySearch(access: SubscriptionAccessState): string {
  const param = access.recoveryParam ?? "subscription_recovery=1";
  return `?tab=billing&${param}`;
}

export function canManageSubscriptionForRole(role: string | null | undefined): boolean {
  return ["OWNER", "ADMIN", "ACCOUNTANT"].includes((role ?? "").toUpperCase());
}

export function canRunSubscriptionAction(input: {
  role: string | null | undefined;
  firmSuspended: boolean;
  action: SubscriptionManagementAction;
}): boolean {
  const role = (input.role ?? "").toUpperCase();
  if (!canManageSubscriptionForRole(role)) return false;
  if (input.action === "checkout" && input.firmSuspended) return false;
  if (input.action === "cancel" && role !== "OWNER") return false;
  return true;
}
